import { Property } from "types/landlord/address";
import { CurrentTenantsSection } from "./CurrentTenantsSection";
import { GeneralSection } from "./GeneralSection";
import { ListingAppearanceSection } from "./ListingAppearanceSection";
import { RentalInfoSection } from "../../../shared/app/RentalInfoSection";
import { PlanSection } from "./PlanSection";
import { PropertyKind } from "shared/types";
import { ScreeningTemplateSection } from "./ScreeningTemplateSection";
import { ListingTemplateSection } from "./ListingTemplateSection";
import { AmenitiesSection } from "./AmenitiesSection";
import { FirstUnitAmenitiesSection } from "./FirstUnitAmenitiesSection";
import { PhotosSection } from "./PhotosSection";
import { BrandingSection } from "./BrandingSection";
import { ListingFraudAlertsSection } from "./ListingFraudAlertsSection";
import { CommunicationDefaultsSection } from "./CommunicationDefaultsSection";
import { Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { ListingSection } from "views/properties-id-units-id/components/ListingSection";
import { LockboxSection } from "views/properties-id-units-id/components/LockboxSection";

interface PropertyDetailsProps {
  property: Property;
}

export const PropertyDetails = ({ property }: PropertyDetailsProps) => {
  const { type } = useAuthContext();

  // `first_unit` is set by the progressive SummaryEntity; `units[0]` by the
  // slim default. Both can coexist — read from whichever is available.
  const firstUnit = property.first_unit ?? property.units?.[0];
  const unitCount = property.total_units ?? property.units?.length ?? 0;
  const hasUnits = unitCount > 0;
  const singleFamilyLike =
    property.kind === PropertyKind.SingleFamily ||
    (!property.kind && unitCount === 1);

  const amenities =
    property.kind === PropertyKind.SingleFamily && firstUnit
      ? firstUnit.amenities
      : property.amenities;

  const amenitiesEligible =
    (property.kind &&
      [PropertyKind.SingleFamily, PropertyKind.MultiFamily].includes(
        property.kind
      )) ||
    type.isPmsListingSyncEnabled;

  const singleFamilyId =
    property.kind === PropertyKind.SingleFamily && firstUnit
      ? firstUnit.id
      : undefined;

  return (
    <div>
      <div className="space-y-4 max-w-[822px] w-full">
        {property.kind === PropertyKind.SingleFamily && firstUnit ? (
          <ListingSection
            propertyKind={property.kind}
            propertyId={property.id}
            unit={firstUnit}
          />
        ) : null}
        <Show if={type.isListingFraudAlerts}>
          <ListingFraudAlertsSection property={property} />
        </Show>
        <Show if={type.isListingSettings}>
          <ListingAppearanceSection property={property} />
          <ListingTemplateSection
            propertyId={property.id}
            prescreeningTemplateId={property.prescreening_template?.identifier}
          />
        </Show>
        <Show if={type.isBoomScreen}>
          <ScreeningTemplateSection
            hasPropertyGroup={Boolean(property.property_group)}
            propertyId={property.id}
            propertyName={property.name}
            verificationTemplateId={property.verification_template?.id}
            usePropertyGroupVerificationTemplate={Boolean(
              property.use_property_group_verification_template
            )}
            templateOptions={property.template_options}
          />
        </Show>
        <Show if={type.isCommunicationSms && type.isBoomCRM}>
          <CommunicationDefaultsSection propertyId={property.id} />
        </Show>
        <GeneralSection property={property} />
        {singleFamilyLike && hasUnits && firstUnit && (
          <>
            {property.prescreening_template?.showing_type === "self_guided" && (
              <LockboxSection
                unit={firstUnit}
                propertyId={property.id}
                propertyTimezone={property.timezone}
              />
            )}
            <RentalInfoSection propertyId={property.id} unit={firstUnit} />
            <PlanSection
              propertyId={property.id}
              unitId={firstUnit.id}
              plan={firstUnit.plan}
              dimensions={firstUnit.dimensions}
            />
          </>
        )}

        <CurrentTenantsSection
          propertyId={property.id}
          initialTenants={property.tenant_contacts}
        />
        <Show if={amenitiesEligible}>
          {singleFamilyId ? (
            <FirstUnitAmenitiesSection
              propertyId={property.id}
              unitId={singleFamilyId}
            />
          ) : (
            <AmenitiesSection
              amenities={amenities || []}
              propertyId={property.id}
              petPolicy={property.pet_policy}
              propertyKind={property.kind}
              property={property}
            />
          )}
          <PhotosSection
            propertyId={property.id}
            propertyMedia={property.media}
            singleFamilyId={singleFamilyId}
          />
        </Show>
        <BrandingSection property={property} />
      </div>
    </div>
  );
};
